'use client';

import { useEffect, useRef, useState } from 'react';

import PauseIcon from '@/assets/icons/pause-solid-full.svg';
import PlayIcon from '@/assets/icons/play.svg';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils/cn';

interface I_VideoPlayerProps {
  videoSrc: string;
  posterSrc?: string;
  className?: string;
}

export function VideoPlayer({
  videoSrc,
  posterSrc,
  className = ''
}: I_VideoPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [showControl, setShowControls] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const video = videoRef.current;

    if (!video) {
      return;
    }

    Array.from(video.textTracks).forEach(track => {
      track.mode = 'disabled';
    });
  }, []);

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
    }
  };

  return (
    <div
      className={cn(
        'relative aspect-video w-full overflow-hidden group shadow-lg',
        className
      )}
    >
      <video
        ref={videoRef}
        src={videoSrc}
        poster={posterSrc}
        playsInline
        preload="none"
        controls={showControl}
        className="w-full h-full object-cover cursor-pointer"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onMouseEnter={() => {
          setShowControls(true);
        }}
        onMouseLeave={() => {
          setShowControls(false);
        }}
      >
        <track
          kind="captions"
          src="/assets/captions/liquidity_pool_en.vtt"
          srcLang="en"
          label="English"
          hidden
        />
      </video>
      <Button
        size="icon"
        variant="link"
        onClick={handlePlayPause}
        aria-label={isPlaying ? 'Pause video' : 'Play video'}
        className="absolute inset-0 top-1/2 -translate-y-1/2 transition-opacity duration-300 flex items-center justify-center focus:ring-0"
        icon={
          <div
            className={cn(
              'w-12 h-12 rounded-full shadow-[0px_4px_20px_0px_rgba(0,0,0,0.50)] flex justify-center items-center bg-primary transition-opacity duration-300 pointer-events-auto',
              isPlaying ? 'opacity-0 md:group-hover:opacity-100' : 'opacity-100'
            )}
          >
            {isPlaying ? <PauseIcon /> : <PlayIcon />}
          </div>
        }
      />
    </div>
  );
}
